import Extension, {ExtensionSlug, ExtensionType, ProgramExtension} from "@/core/interfaces/extension/Extension";
import {RouteLocation, Router} from "vue-router";
import ExtensionService from "@/extension/services/ExtensionService";
import {EXTENSION_DETAILS_QUERY_KEY} from "@/extension/extensionRouter";

/**
 * Extension navigation service.
 *
 * Service responsible for navigating to extension details pages.
 */
const ExtensionNavigationService = () => {
    return {
        /**
         * Navigate to an extension details page.
         */
        navigate(extension: Extension, router: Router, route: RouteLocation, options?: { consumerTab?: boolean }) {
            if (!extension.enabled) {
                this.showExtensionDetails(extension.id, router, route);
                return;
            }

            // A module still to be set up leads to its own page, where its
            // description and its configurator sit together. Once the pharmacy
            // has been through it, the same click opens the module itself.
            if (extension.configurator && !extension.configured) {
                this.showExtensionConfiguration(extension.id, router);
                return;
            }

            if (ExtensionService.isProgramExtension(extension)) {
                this.navigateToProgram(extension, router, options);
                return;
            }

            if (extension.extendable_type === ExtensionType.PACK) {
                this.navigateToPack(extension, router);
                return;
            }

            throw new Error('Unsupported extension type');
        },

        /**
         * Navigate to an extension of type pack.
         */
        navigateToPack(extension: Extension, router: Router) {
            switch (extension.slug) {
                case ExtensionSlug.DISRUPTION_LOGISTICS:
                    router.push({
                        name: 'disruption-logistics'
                    });
                    break;
                case ExtensionSlug.CLICK_COLLECT:
                    router.push({
                        name: 'patient-administration.orders'
                    });
                    break;
                case ExtensionSlug.ASAFO:
                    router.push({
                        name: 'asafo'
                    });
                    break;
                case ExtensionSlug.SMS_PREMIUM:
                    router.push({
                        name: 'patient-administration.communication.customers'
                    });
                    break;
            }
        },

        /**
         * Navigate to an extension of type program-bundle.
         *
         * If bundle contain more than one program, navigate to the bundle detail page.
         * If bundle contain only one program, navigate to the program detail page.
         */
        navigateToProgram(extension: ProgramExtension, router: Router, options?: { consumerTab?: boolean }) {
            if (extension.program_bundle.programs.length > 1) {
                router.push({
                    name: 'program-bundle.detail',
                    params: {
                        programBundleId: extension.program_bundle.id
                    }
                });
                return;
            }

            if (extension.program_bundle.programs.length === 1) {
                const id = extension.program_bundle.programs[0].id;

                router.push({
                    name: options?.consumerTab ? 'program.detail.consumers' : 'program.detail.content',
                    params: {
                        programId: id
                    }
                });

                return;
            }

            router.push({
                name: options?.consumerTab ? 'program.detail.consumers' : 'program.detail.content',
                params: {
                    programId: extension.extendable_id
                }
            });
        },

        /**
         * Open the page of one module: its description and its configurator.
         *
         * @param extensionId The extension id.
         * @param router The router.
         */
        showExtensionConfiguration(extensionId: number, router: Router) {
            router.push({
                name: 'extension.detail',
                params: {extensionId: String(extensionId)}
            });
        },

        /**
         * Open the extension details modal by setting the query parameter.
         * @param extensionId
         * @param router
         * @param route
         */
        showExtensionDetails(extensionId: number, router: Router, route: RouteLocation) {
            router.replace({
                query: {
                    ...(route?.query ?? {}),
                    [EXTENSION_DETAILS_QUERY_KEY]: extensionId
                }
            });
        }
    };
};

export default ExtensionNavigationService();
